import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import GiveButterLandingHelmet from 'common/helmets/GiveButterLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import GiveButterChangelog from 'img/landing/givebutter-changelog.webp';
import GiveButterWordmark from 'img/landing/givebutter-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const GiveButterLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed GiveButter Case Study');
  }, [trackEvent]);

  return (
    <div className="landingCustomersPage">
      <GiveButterLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How GiveButter crushed their NPS and predicted the future with Canny" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/LRjoALiqk0I"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              GiveButter is on a mission to help nonprofits raise funds easier. Their platform
              centralizes donation forms, donation pages, events, auctions, donor management (CRM),
              email, texting, and&nbsp;more.
            </p>
            <p>
              Jesse Sandala is the director of product at GiveButter. That means he’s making sure
              that GiveButter is the best it could be. He does it by working with lots of different
              teams and, of course, managing user&nbsp;feedback.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>From the beginning, GiveButter strived for product-led&nbsp;growth.</p>
            <p className="quote">
              &ldquo;We want to let customer feedback really drive our roadmap. Not only on small
              enhancements and changes, but also on our major&nbsp;features.&rdquo;
            </p>
            <p>
              GiveButter’s also focused on engaging with their customers. They want to make sure
              that they’re building only the right features and not wasting any&nbsp;effort.
            </p>
            <p>
              Finally, GiveButter needed to give their customers an avenue to share what’s on
              their&nbsp;minds.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>With Canny, GiveButter can really understand their&nbsp;customers.</p>
            <p className="quote">
              &ldquo;It’s pretty cool when you see a request with lengthy back and forth. It means
              that people care, which is always a good&nbsp;problem.&rdquo;
            </p>
            <p>Jesse uses that information and insights&nbsp;to:</p>
            <ul>
              <li>Decide what to build&nbsp;next</li>
              <li>Determine how to do&nbsp;it</li>
              <li>Keep their users updated on&nbsp;progress</li>
              <li>Announce updates through the&nbsp;changelog</li>
            </ul>
            <p>
              The product team isn’t the only one that uses Canny. GiveButter’s sales team reaps the
              benefits,&nbsp;too!
            </p>
            <p className="quote">
              &ldquo;As the sales team is working with the prospect, they use Canny to add new
              requests and upvote existing ones on behalf of those prospects. That really signals to
              us that we should prioritize certain&nbsp;features.&rdquo;
            </p>
            <p>
              The sales team also uses the Hubspot integration with Canny. They attribute deal and
              revenue data to specific feature requests and help prioritize them that&nbsp;way.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              In the fall of 2022, GiveButter introduced Auctions – a new tool that received over
              600 votes on Canny! Since GiveButter launched this tool in September, they’ve seen
              even more engagement with their platform&nbsp;overall.
            </p>
            <p className="quote">
              &ldquo;Canny gives us a strong sense of what customers want – both on the big picture
              features and “quality of life” ones. The latter makes customers’ daily usage of
              GiveButter easier and more&nbsp;efficient.&rdquo;
            </p>
            <p>
              When Jesse’s team started building auctions, they used Canny to not only see the
              number of votes, but also determine prioritization&nbsp;criteria.
            </p>
            <p className="quote">
              &ldquo;We assigned the effort score and strategic importance to achieve a more
              balanced ranking for our&nbsp;roadmap.&rdquo;
            </p>
            <p>
              All of this groundwork helps with new feature adoption too – they can almost predict
              how well the new feature will be received. In a way, Canny helps GiveButter see the
              future. And, that’s exactly what they did with their new Auction component. Pretty
              cool,&nbsp;right?
            </p>
            <p>
              Today, Canny really helps shape the roadmap for GiveButter. It also helps the team
              keep their customers up to date on the progress and the direction of the product as a
              whole. For that, GiveButter uses{' '}
              <a target="_blank" href="https://canny.io/features/product-changelog" rel="noopener">
                Canny’s Changelog&nbsp;tool
              </a>
              .
            </p>
            <LazyLoadedImage src={GiveButterChangelog} alt="GiveButter Changelog" />
            <p>The team enjoys having a clear and shared vision, thanks to&nbsp;Canny.</p>
            <p>Not just the team at GiveButter loves Canny. Their customers value it&nbsp;too.</p>
            <p className="quote">
              &ldquo;People always love it when something they’ve voted on or commented on is built.
              In Q1, our NPS score was 75, so this is a great indicator for us. Our customers know
              and tell us that GiveButter really&nbsp;listens.&rdquo;
            </p>
            <p>
              We’re happy to help GiveButter serve their customers better! And, we’d love to help
              you do the&nbsp;same.
            </p>
            <p>Give Canny a try for free today and join awesome companies like&nbsp;GiveButter.</p>
          </div>
          <div className="right">
            <LazyLoadedImage
              className="giveButterWordmark"
              src={GiveButterWordmark}
              alt="GiveButter Logo"
            />
            Givebutter is the most-loved nonprofit fundraising platform, empowering millions of
            changemakers to raise more, pay less, and give&nbsp;better.
            <a href="https://givebutter.com/" rel="noopener" target="_blank">
              givebutter.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>40</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2017</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2020</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>35,101</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default hot(GiveButterLanding);
